// enums
import { ErrorCode } from 'enums';

// services
import { COOKIE_ACCESS_TOKEN, COOKIE_REFRESH_TOKEN, cookieStorage } from 'services/cookie';

// libraries
import axios from 'axios';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';

export const extractErrorMsg = (error) => {
  if (!error.response) {
    return '서버에 접속할 수 없습니다';
  } else {
    return error.response.data.message || '에러 발생';
  }
};

const source = axios.CancelToken.source();

export const clearUserToken = () => {
  cookieStorage.clearAllCookies();
};

export const logout = () => {
  cookieStorage.clearAllCookies();
  window.localStorage.clear();
  window.sessionStorage.clear();
  window.location.replace('/sign-in');
};

class AxiosInstanceCreator {
  #instance;

  constructor(config) {
    this.#instance = axios.create({
      baseURL: `${import.meta.env.VITE_API_URL}/v1/api`,
      withCredentials: true,
      ...config
    });

    this.#instance.defaults.cancelToken = source.token;

    this.interceptors();
  }

  interceptors() {
    this.#instance.interceptors.request.use(async (config) => {
      const token = cookieStorage.getCookie(COOKIE_ACCESS_TOKEN);
      if (
        (token === undefined || token === null || token === '') &&
        window.location.pathname !== '/sign-in' &&
        !window.location.pathname.startsWith('/pmdm')
      ) {
        console.warn(222);
        console.log('== 토큰 unknown 에러 발생 ==');
        logout();
        return;
      }
      if (!config.headers['Authorization']) {
        sessionStorage.getItem(COOKIE_ACCESS_TOKEN);

        if (token !== null && token !== 'undefined' && token !== undefined) {
          const refreshToken = cookieStorage.getCookie(COOKIE_REFRESH_TOKEN);
          const decodedToken = jwtDecode(token).exp;
          const currentTime = dayjs().unix();
          const isExpired = decodedToken < currentTime;

          // 토큰 만료시 재발급 함수 추가
          if (refreshToken && isExpired) {
            try {
              const response = await axios.get(`/v1/api/token`, {
                headers: {
                  REFRESH_TOKEN: `Bearer ${refreshToken}`,
                  Authorization: `Bearer ${token}`
                }
              });

              const { ACCESS_TOKEN, REFRESH_TOKEN } = response?.data.resultObject;

              cookieStorage.setCookie(COOKIE_ACCESS_TOKEN, ACCESS_TOKEN);
              cookieStorage.setCookie(COOKIE_REFRESH_TOKEN, REFRESH_TOKEN);

              Object.assign(config.headers, {
                Authorization: `Bearer ${ACCESS_TOKEN}`
              });
            } catch (e) {
              console.log('== 토큰 재발급 에러 발생 == : ', e);

              logout();
            }
          } else {
            Object.assign(config.headers, {
              Authorization: `Bearer ${token}`
            });
          }
        }
      }

      if (!config.headers['Content-Type']) {
        Object.assign(config.headers, {
          'Content-Type': 'application/json'
        });
      }

      if (!config.headers['Accept']) {
        Object.assign(config.headers, {
          Accept: 'application/json'
        });
      }

      if (!config.headers['X-PKG-ID']) {
        Object.assign(config.headers, {
          'X-PKG-ID': 'saf4s'
        });
      }

      return config;
    });

    this.#instance.interceptors.response.use(
      (res) => {
        if (res.data.code && res.data.code !== 200) {
          throw new Error(res.data.message).message;
        }

        return res;
      },

      (error) => {
        if (error.response && error.response.status === 504) {
          console.log('서버가 일시적으로 사용 불가능합니다.');

          const result = {
            ...error.response.data,
            message: '서버가 일시적으로 사용 불가능합니다. 잠시 후 다시 시도해주세요.',
            statusCode: 504
          };

          throw result;
        }

        if (axios.isCancel(error)) {
          clearUserToken();

          throw error;
        } else {
          if (error.response.status === ErrorCode.AUTHORIZATION_REQUIRED) {
            clearUserToken();
            return;
          }

          const result = {
            ...error.response.data,
            message: extractErrorMsg(error)
          };

          throw result;
        }
      }
    );
  }

  create() {
    return this.#instance;
  }
}

export default AxiosInstanceCreator;
